import Handlebars from 'handlebars';

import { OrderTemplate } from '../../model/order-template';
import OrderTemplateModals from './order-template-modals';

interface ToolbarButtonsConfig {
  showDeleteButton: boolean;
  showDuplicateButton: boolean;
  showSaveButton: boolean;
  showEditButton: boolean;
  showCancelEditButton: boolean;
  showBulkAddButton: boolean;
}

export default class OrderTemplateToolbar {

  private readonly toolbarTemplate: Handlebars.TemplateDelegate<any>;

  private editBtn?: JQuery;
  private duplicateBtn?: JQuery;
  private deleteTemplateBtn?: JQuery;
  private bulkAddBtn?: JQuery;
  private saveBtn?: JQuery;
  private deleteEntriesBtn?: JQuery;
  private cancelEditBtn?: JQuery;

  private readonly orderTemplateModals: OrderTemplateModals;
  private readonly name: JQuery;
  private readonly description: JQuery;

  private readonly toolbarContainerSelector: string = '.js-toolbarContainer';

  constructor(
    private container: JQuery,
    private orderTemplate: OrderTemplate,
    private editModeActive: boolean,
    private bulkAddToCartCallback: () => JQuery.jqXHR,
    private renderEntriesCallback: () => void,
    private saveCallback: () => void,
    private cancelCallback: () => void,
    private toggleEditCallback: () => void,
  ) {
    this.orderTemplateModals = new OrderTemplateModals(container);
    this.name = container.find('.js-name');
    this.description = container.find('.js-description');

    this.toolbarTemplate = Handlebars.compile($('.js-toolbarTemplate').html());

    this.setEditMode(this.editModeActive);
    this.bindNameChange();
    this.bindDescriptionChange();
    this.setupDeleteEntries();
  }

  setupDeleteEntries(): void {
    this.deleteEntriesBtn = this.container.find('.js-openDeleteEntriesModal');
    this.bindDeleteEntries();
  }

  setEditMode(active: boolean): void {
    this.editModeActive = active;
    this.toggleNameInput();
    this.toggleDescriptionInput();
    this.renderToolbar();
  }

  private toggleNameInput(): void {
    this.name.prop('disabled', !this.editModeActive);
  }

  private toggleDescriptionInput(): void {
    this.description.prop('disabled', !this.editModeActive);
  }

  private bindEditBtnClick(): void {
    this.editBtn?.on('click', () => {
      this.toggleEditCallback();
    });
  }

  private bindDuplicateBtnClick(): void {
    if (this.duplicateBtn && !this.duplicateBtn.prop('disabled')) {
      this.duplicateBtn.on('click', () => {
        this.orderTemplateModals.bindOpenDuplicateModal(this.orderTemplate);
      });
    }
  }

  private bindDeleteTemplateBtnClick(): void {
    this.deleteTemplateBtn?.on('click', () => {
      this.orderTemplateModals.bindOpenDeleteModal(this.orderTemplate);
    });
  }

  private bindBulkAddBtnClick(): void {
    this.bulkAddBtn?.on('click', () => {
      this.bulkAddToCartCallback();
    });
  }

  private bindSaveBtnClick(): void {
    this.saveBtn?.on('click', () => {
      this.saveCallback();
    });
  }

  private bindDeleteEntries(): void {
    this.deleteEntriesBtn?.on('click', () => {
      this.orderTemplateModals.bindOpenDeleteEntriesModal(this.orderTemplate, this.renderEntriesCallback);
    });
  }

  private bindCancelEdit(): void {
    this.cancelEditBtn?.on('click', () => {
      this.cancelCallback();
    });
  }

  private bindNameChange(): void {
    this.name.on('change', (e) => {
      this.orderTemplate.setName($(e.currentTarget).val() as string);
    });
  }

  private bindDescriptionChange(): void {
    this.description.on('change', (e) => {
      this.orderTemplate.setDescription($(e.currentTarget).val() as string);
    });
  }

  private getToolbarActionsConfiguration(): ToolbarButtonsConfig {
    return {
      showDeleteButton: true,
      showDuplicateButton: !this.editModeActive,
      showSaveButton: this.editModeActive,
      showEditButton: !this.editModeActive,
      showCancelEditButton: this.editModeActive,
      showBulkAddButton: !this.editModeActive && this.orderTemplate.getEntries().length > 0,
    };
  }

  private renderToolbar(): void {
    const config: ToolbarButtonsConfig = this.getToolbarActionsConfiguration();
    $(this.toolbarContainerSelector).html(this.toolbarTemplate(config));

    this.setupToolbar();
  }

  private setupToolbar(): void {
    const container: JQuery = $(this.toolbarContainerSelector);

    this.editBtn = container.find('.js-orderTemplateDetailEditBtn');
    this.duplicateBtn = container.find('.js-orderTemplateDetailDuplicateBtn');
    this.deleteTemplateBtn = container.find('.js-orderTemplateDetailDeleteBtn');
    this.bulkAddBtn = container.find('.js-orderTemplateDetailBulkAddBtn');
    this.saveBtn = container.find('.js-orderTemplateDetailSaveBtn');
    this.cancelEditBtn = container.find('.js-orderTemplateDetailCancelEditBtn');

    this.bindEditBtnClick();
    this.bindDuplicateBtnClick();
    this.bindDeleteTemplateBtnClick();
    this.bindBulkAddBtnClick();
    this.bindSaveBtnClick();
    this.bindCancelEdit();
  }

}
